import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * CookLevelBadge — inline chip showing a public cook's level + top badge.
 *
 * Renders next to a recipe's "creator_name" byline on public recipe/cookbook
 * pages, giving casual browsers social proof about the person behind the
 * recipe. Clicking it deep-links into `/cook/:user_id` (public profile).
 *
 * Props
 *   userId  — required, the owner_id of the recipe/cookbook
 *   variant — "chip" (default, tiny inline pill) | "card" (fatter, for
 *             leaderboard/profile use)
 *
 * Fetch is cached per-mount; multiple mounts of the same user on one page
 * (e.g. a cookbook listing 20 recipes by the same person) hit /gamification
 * separately for now — cheap because computeUserStats is one aggregation.
 * If it becomes a bottleneck we'll add a client-side memo cache.
 */
export default function CookLevelBadge({ userId, variant = "chip" }) {
  const { flags, loading: flagsLoading } = useSiteFlags();
  const { user } = useAuth();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || flagsLoading) return;
    // Master feature-flag gate — no request at all when off (unless staff).
    if (!flags.gamification_enabled && !isStaff) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/gamification/profile/${userId}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, flags.gamification_enabled, flagsLoading, isStaff]);

  if (loading || !data) return null;
  // Master flag off + not staff — render nothing (safe).
  if (!flags.gamification_enabled && !isStaff) return null;
  // Cooks with zero public recipes get no chip — no reason to advertise a
  // Novice-with-no-badges profile.
  if ((data.public_recipe_count || 0) === 0) return null;
  // A user with no public username shouldn't have a clickable chip anywhere
  // (would leak the cook profile linking back to a "@Anonymous Cook" page).
  if (!data.username || data.username_suspended) return null;

  const topBadge = (data.badges && data.badges[data.badges.length - 1]) || null;
  const level = data.level || {};
  const displayName = data.display_name || data.username;

  if (variant === "card") {
    return (
      <Link
        to={`/cook/${userId}`}
        data-testid={`cook-level-card-${userId}`}
        className="inline-flex items-center gap-3 rounded-2xl bg-white/70 backdrop-blur border border-rf-ink/10 px-4 py-3 hover:border-rf-orange/60 transition-colors"
      >
        <div className="w-11 h-11 rounded-full bg-rf-amber/30 flex items-center justify-center text-2xl">
          {topBadge?.emoji || "🍳"}
        </div>
        <div className="text-left">
          <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">
            @{displayName} · Lvl {level.level}
          </p>
          <p className="text-sm font-bold text-rf-ink">
            {data.public_recipe_count} public {data.public_recipe_count === 1 ? "recipe" : "recipes"} · {data.badges.length}{" "}
            {data.badges.length === 1 ? "badge" : "badges"}
          </p>
        </div>
      </Link>
    );
  }

  // Compact chip variant (default) — sits inline next to "creator_name".
  return (
    <Link
      to={`/cook/${userId}`}
      data-testid={`cook-level-chip-${userId}`}
      title={`@${displayName} · ${level.name} · ${data.public_recipe_count} public ${data.public_recipe_count === 1 ? "recipe" : "recipes"}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-rf-amber/20 border border-rf-amber/40 px-2.5 py-1 text-xs font-bold text-rf-ink hover:bg-rf-amber/40 transition-colors align-middle"
    >
      {topBadge && <span aria-hidden>{topBadge.emoji}</span>}
      <span>@{displayName}</span>
      <span className="text-rf-ink-soft">·</span>
      <span>{level.name}</span>
    </Link>
  );
}
